import { Close as CloseIcon } from '@mui/icons-material'
import { Box, IconButton, Paper, Table, TableBody, TableCell, TableRow, Typography, useTheme } from '@mui/material'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { UsageStats } from '../../api/agent/useAgentChat'

export interface SessionUsage {
  messages: number
  inputTokens: number
  outputTokens: number
  cacheWriteTokens: number
  cacheReadTokens: number
  actualCost: number
  uncachedCost: number
  savedCost: number
}

interface UsagePanelProps {
  usage: SessionUsage
  pos: { x: number; y: number }
  size: { w: number; h: number }
  onClose: () => void
}

const PANEL_WIDTH = 300
const PANEL_MIN_WIDTH = 240
const PANEL_GAP = 8

function fmt(n: number) {
  return n.toLocaleString('en-US')
}

function fmtCost(n: number) {
  return `$${n.toFixed(4)}`
}

export function accumulateUsage(prev: SessionUsage | null, incoming: UsageStats): SessionUsage {
  return {
    messages: (prev?.messages ?? 0) + 1,
    inputTokens: (prev?.inputTokens ?? 0) + incoming.inputTokens,
    outputTokens: (prev?.outputTokens ?? 0) + incoming.outputTokens,
    cacheWriteTokens: (prev?.cacheWriteTokens ?? 0) + incoming.cacheWriteTokens,
    cacheReadTokens: (prev?.cacheReadTokens ?? 0) + incoming.cacheReadTokens,
    actualCost: (prev?.actualCost ?? 0) + incoming.actualCost,
    uncachedCost: (prev?.uncachedCost ?? 0) + incoming.uncachedCost,
    savedCost: (prev?.savedCost ?? 0) + incoming.savedCost,
  }
}

const UsagePanel: React.FC<UsagePanelProps> = ({ usage, pos, size, onClose }) => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight })

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const panelStyle = useMemo(() => {
    const chatCenterX = pos.x + size.w / 2
    const anchorSide = chatCenterX > viewport.w / 2 ? 'left' : 'right'

    let width = PANEL_WIDTH
    let left: number

    if (anchorSide === 'right') {
      const spaceRight = viewport.w - (pos.x + size.w + PANEL_GAP)
      width = Math.min(PANEL_WIDTH, Math.max(PANEL_MIN_WIDTH, spaceRight - 8))
      left = pos.x + size.w + PANEL_GAP
    } else {
      const spaceLeft = pos.x - PANEL_GAP
      width = Math.min(PANEL_WIDTH, Math.max(PANEL_MIN_WIDTH, spaceLeft - 8))
      left = pos.x - width - PANEL_GAP
    }

    left = Math.max(8, Math.min(left, viewport.w - width - 8))
    const maxHeight = viewport.h - pos.y - 16
    const height = Math.min(size.h, maxHeight)

    return { left, top: pos.y, width, height }
  }, [pos.x, pos.y, size.w, size.h, viewport.w, viewport.h])

  const savingsPct = usage.uncachedCost > 0 ? Math.round((usage.savedCost / usage.uncachedCost) * 100) : 0

  const rows: Array<{ label: string; value: string | number; variant?: 'bold' | 'muted' | 'success' }> = [
    { label: 'Messages', value: usage.messages },
    { label: 'Input tokens', value: fmt(usage.inputTokens) },
    { label: 'Output tokens', value: fmt(usage.outputTokens) },
    { label: 'Cache writes', value: fmt(usage.cacheWriteTokens) },
    { label: 'Cache reads', value: fmt(usage.cacheReadTokens) },
    { label: 'Actual cost', value: fmtCost(usage.actualCost), variant: 'bold' },
    { label: 'Without caching', value: fmtCost(usage.uncachedCost), variant: 'muted' },
    { label: `Saved (${savingsPct}%)`, value: fmtCost(usage.savedCost), variant: 'success' },
  ]

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: panelStyle.top,
        left: panelStyle.left,
        width: panelStyle.width,
        maxHeight: panelStyle.height,
        zIndex: 1299,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: theme.palette.background.paper,
        boxShadow: `4px 1px 16px ${theme.palette.background.section}`,
        pt: pinned ? 6 : 0,
        transition: 'padding 0.3s ease',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 36,
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, fontSize: '0.75rem' }}>
          Session Usage
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary', p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Stats table */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 0.75 }}>
        <Table size="small">
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.label} sx={{ '& td': { border: 0, py: 0.3 } }}>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.72rem', pl: 0 }}>{row.label}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    pr: 0,
                    fontSize: '0.72rem',
                    fontFamily: row.variant ? 'inherit' : 'monospace',
                    fontWeight: row.variant === 'bold' || row.variant === 'success' ? 700 : 400,
                    color:
                      row.variant === 'success'
                        ? 'success.main'
                        : row.variant === 'muted'
                        ? 'text.disabled'
                        : 'text.primary',
                  }}
                >
                  {String(row.value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  )
}

export default UsagePanel
